import {getAuth} from 'firebase/auth'

export async function getFirebaseIdTokenOrThrow(): Promise<string> {
  const user = getAuth().currentUser
  if (!user) {
    throw new Error('Not authenticated (no current Firebase user)')
  }
  return await user.getIdToken()
}

export async function authFetch(input: RequestInfo | URL, init: RequestInit = {}) {
  const token = await getFirebaseIdTokenOrThrow()
  const requestInput: RequestInfo = input instanceof URL ? input.toString() : input
  const headers = new Headers(init.headers || {})
  headers.set('Authorization', `Bearer ${token}`)
  return fetch(requestInput, {...init, headers})
}

/**
 * An HTTP/API failure surfaced by authFetchJson. `status` is the HTTP status
 * (0 when the request never got a response - offline, DNS, CORS, no auth).
 */
export class HttpError extends Error {
  status: number
  body: unknown
  constructor(message: string, status: number, body?: unknown) {
    super(message)
    this.name = 'HttpError'
    this.status = status
    this.body = body
    // TS compiles to ES5 in some targets; keep instanceof working.
    Object.setPrototypeOf(this, HttpError.prototype)
  }
}

// Pull the server's human-readable failure text out of a parsed body.
// Cloud Functions here answer { ok:false, error } or { error } or { message }.
function serverErrorText(body: any): string {
  if (!body || typeof body !== 'object') return ''
  const raw = body.error ?? body.message
  if (typeof raw === 'string') return raw.trim()
  if (raw && typeof raw === 'object' && typeof raw.message === 'string') return raw.message.trim()
  return ''
}

/**
 * authFetch + tolerant JSON parse that THROWS on failure.
 *
 * authFetch is a bare fetch wrapper: it resolves on 4xx/5xx, so callers that
 * forget to test resp.ok report success for writes that never happened. This
 * variant turns both failure shapes into a thrown HttpError:
 *   - a non-2xx status, or
 *   - a 2xx whose JSON body says { ok:false } (some endpoints answer 200 with
 *     a per-item failure inside).
 * The message is the server's own error/message text, else "HTTP <status>".
 * A 2xx with a non-JSON/empty body resolves to null (caller decides whether
 * silence is acceptable - writes should use authPostConfirmed instead).
 */
export async function authFetchJson<T = any>(input: RequestInfo | URL, init: RequestInit = {}): Promise<T> {
  let resp: Response
  try {
    resp = await authFetch(input, init)
  } catch (e) {
    if (e instanceof HttpError) throw e
    // fetch() rejects with a bare TypeError ("Failed to fetch" / "Load failed")
    // for every transport failure - say what it means to a human.
    const msg = e instanceof TypeError ? 'network error - check your connection' : ((e as any)?.message || 'request failed')
    throw new HttpError(msg, 0)
  }
  let body: any = null
  try {
    const text = await resp.text()
    body = text ? JSON.parse(text) : null
  } catch {
    body = null
  }
  if (!resp.ok) {
    throw new HttpError(serverErrorText(body) || `HTTP ${resp.status}`, resp.status, body)
  }
  if (body && typeof body === 'object' && body.ok === false) {
    throw new HttpError(serverErrorText(body) || 'request failed', resp.status, body)
  }
  return body as T
}

/**
 * POST a JSON payload to a write endpoint and require the server's explicit
 * { ok:true }. For Graph-backed actions (delete, junk, flag, read state) a
 * 200 with an empty or foreign body (proxy error page, stale deploy) must not
 * be reported as "done" - so silence is treated as failure here.
 */
export async function authPostConfirmed<T = any>(url: string, payload: unknown): Promise<T> {
  const res = await authFetchJson<any>(url, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(payload ?? {}),
  })
  if (!res || typeof res !== 'object' || res.ok !== true) {
    throw new HttpError('the server did not confirm the change', 200, res)
  }
  return res as T
}
